import { useCallback } from 'react';

export type EntityType = 'account' | 'contact' | 'opportunity' | 'view';

export interface RecentItem {
  id: string;
  metadata?: {
    city?: string;
    companyName?: string;
    email?: string;
    function?: string;
    industryName?: string;
    investorCategory?: string;
    stage?: string;
    status?: string;
  };
  name: string;
  type: EntityType;
  viewedAt: number;
}

const STORAGE_KEY = 'opstream_recent_items';
const MAX_ITEMS = 30;

/**
 * Hook for managing recently viewed items in localStorage
 */
export function useRecentItems() {
  const getRecentItems = useCallback((limit?: number): RecentItem[] => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];

      const items: RecentItem[] = JSON.parse(stored);

      // Sort by viewedAt (most recent first)
      const sorted = items.sort((a, b) => b.viewedAt - a.viewedAt);

      return limit ? sorted.slice(0, limit) : sorted;
    } catch (error) {
      console.error('Error reading recent items:', error);
      return [];
    }
  }, []);

  const addRecentItem = useCallback(
    (item: Omit<RecentItem, 'viewedAt'>) => {
      try {
        const items = getRecentItems();

        // Remove existing item with same ID and type
        const filtered = items.filter(
          i => !(i.id === item.id && i.type === item.type)
        );

        // Add new item at the beginning
        const newItem: RecentItem = {
          ...item,
          viewedAt: Date.now()
        };

        const updated = [newItem, ...filtered].slice(0, MAX_ITEMS);

        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error('Error adding recent item:', error);
      }
    },
    [getRecentItems]
  );

  const removeRecentItem = useCallback(
    (id: string, type: EntityType) => {
      try {
        const items = getRecentItems();
        const filtered = items.filter(
          item => !(item.id === id && item.type === type)
        );
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
      } catch (error) {
        console.error('Error removing recent item:', error);
      }
    },
    [getRecentItems]
  );

  const clearRecentItems = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing recent items:', error);
    }
  }, []);

  const getRecentItemsByType = useCallback(
    (type: EntityType, limit?: number): RecentItem[] => {
      const items = getRecentItems();
      const filtered = items.filter(item => item.type === type);
      return limit ? filtered.slice(0, limit) : filtered;
    },
    [getRecentItems]
  );

  return {
    addRecentItem,
    clearRecentItems,
    getRecentItems,
    getRecentItemsByType,
    removeRecentItem
  };
}
